import { ArrowRight, Download } from "lucide-react";

const PDF_URL = process.env.REACT_APP_BACKEND_URL + "/api/docs/how-it-works.pdf";

const CRED_ROWS = [
  { label: "Peer-reviewed journal", score: 9.5, bar: "w-[95%]", color: "bg-emerald-400", example: "Nature, The Lancet, IEEE" },
  { label: "Established news outlet", score: 8.4, bar: "w-[84%]", color: "bg-amber-400", example: "Reuters, BBC, Bloomberg" },
  { label: "General web source", score: 6.0, bar: "w-[60%]", color: "bg-zinc-500", example: "blogs, aggregators, forums" },
];

const FLOW = ["Primary Ranking Plaque", "Grounded Facts + Keywords", "Cross-Domain Ranking Plaque", "Multi-Tier Synthesis", "Grounded Conclusion"];

const PIPELINE_STEPS = [
  { n: "01", title: "Primary Research", body: "Deep search on your topic retrieves real peer-reviewed sources. A Math Core Ranking Plaque ranks each source top-to-bottom by combined score — full citation, DOI link, credibility /10, and match %. This ranked list is the foundation everything builds from." },
  { n: "02", title: "Grounded Facts & Keywords", body: "Core verified facts are extracted from the ranked sources, each showing its source and credibility score inline. From those facts, 10-20 precise SEARCH KEYWORDS capture the deepest concepts in the research." },
  { n: "03", title: "Cross-Domain Grounded Search", body: "The keywords drive a search across other scientific and technical domains — also returning real ranked sources shown in a secondary plaque. 5-7 grounded cross-domain facts show exactly how each domain connects back through a shared keyword." },
  { n: "04", title: "Multi-Tier Synthesis", body: "The synthesis is built from all grounded facts in expanding tiers: Tier 1 sits tightly on your primary sources, outer tiers reach into the cross-domain findings. Anything beyond the verified sources is labeled [SYNTHESIS]." },
  { n: "05", title: "Grounded Conclusion", body: "A final conclusion drawn only from the verified facts and ranked sources — the single most defensible, forward-looking statement the evidence supports. Not speculation." },
];

const EXAMPLES = [
  {
    topic: "Neuroplasticity & Learning Retention",
    plaque: [
      "Hebb (1949), Neuroscience        · cred 9.8 · match 97.2%",
      "Kandel (2000), Science           · cred 9.7 · match 96.1%",
      "Draganski (2004), Nature         · cred 9.5 · match 94.3%",
    ],
    facts: [
      "Synaptic strengthening follows repeated activation patterns [Hebb 9.8].",
      "Long-term potentiation requires protein synthesis within 6h of learning [Kandel 9.7].",
      "Structural grey-matter changes occur within weeks of new skill acquisition [Draganski 9.5].",
    ],
    keywords: "synaptic plasticity · long-term potentiation · memory consolidation · protein synthesis · repetition intervals · structural adaptation · neural pathway reinforcement · sleep consolidation · retrieval practice · spaced repetition",
    crossPlaque: [
      "Ebbinghaus (1885), Psychology         · cred 9.3 · match 93.1%",
      "Ericsson (1993), Psychological Review · cred 9.2 · match 91.4%",
    ],
    crossFacts: [
      "Forgetting follows an exponential decay curve reversible through spaced retrieval [Ebbinghaus 9.3] — shared keyword: memory consolidation.",
      "Deliberate practice with immediate feedback accelerates consolidation faster than passive repetition [Ericsson 9.2] — shared keyword: retrieval practice.",
    ],
    tiers: [
      "Tier 1 — biological mechanisms of synaptic strengthening.",
      "Tier 2 — application to learning-system design.",
      "Tier 3 — [SYNTHESIS] implications for AI training feedback loops where spaced-retrieval principles apply to fine-tuning intervals.",
    ],
    conclusion: "Evidence across neuroscience and cognitive psychology confirms retention is determined by retrieval timing and structural repetition, not exposure volume. Systems designed around this principle outperform those optimised for coverage.",
  },
  {
    topic: "Supply Chain Resilience After Geopolitical Disruption",
    plaque: [
      "Sheffi (2005), MIT Press            · cred 9.1 · match 95.4%",
      "WEF Global Risk Report (2023)       · cred 8.9 · match 93.2%",
      "Chopra & Sodhi (2004), MIT Sloan    · cred 9.0 · match 92.8%",
    ],
    facts: [
      "Single-source dependencies raise disruption exposure 3–5× vs dual-source models [Sheffi 9.1].",
      "Geopolitical events now rank as the top supply-chain risk above natural disasters for the first time [WEF 8.9].",
      "Risk pooling across suppliers reduces variance without proportionally increasing cost [Chopra 9.0].",
    ],
    keywords: "redundancy · risk pooling · geographic diversification · nearshoring · inventory buffering · demand signal latency · supplier concentration · resilience investment · systemic fragility · adaptive logistics",
    crossPlaque: [
      "Taleb (2012), Random House                   · cred 8.8 · match 91.0%",
      "Holling (1973), Annual Review of Ecology      · cred 9.2 · match 89.5%",
    ],
    crossFacts: [
      "Antifragile systems gain strength from stressors rather than merely surviving them [Taleb 8.8] — shared keyword: systemic fragility.",
      "Ecosystems with higher biodiversity recover from disruption ~40% faster than monocultures [Holling 9.2] — shared keyword: redundancy.",
    ],
    tiers: [
      "Tier 1 — risk-concentration mechanics in supplier networks.",
      "Tier 2 — portfolio diversification applied to supplier selection.",
      "Tier 3 — [SYNTHESIS] ecological resilience principles predict efficiency-optimised chains become systemically fragile at scale.",
    ],
    conclusion: "The convergence of supply-chain research and ecological resilience theory confirms efficiency and resilience are structurally opposed objectives. Organisations that treat redundancy as waste rather than insurance systematically underestimate tail risk until a disruption reveals the true cost.",
  },
];

export default function HowItWorksPage() {
  return (
    <div data-testid="how-it-works-page" className="max-w-4xl mx-auto p-4 sm:p-6 space-y-8">
      <header className="space-y-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">
          REFERENCE_PANEL
        </div>
        <h1 className="text-3xl sm:text-4xl font-mono tracking-tight text-white">
          How MindStein Works
        </h1>
        <p className="text-sm text-zinc-400 max-w-2xl leading-relaxed">
          Every answer is built on ranked sources, and every leap of reasoning
          is labeled. This is the math behind the trust layer.
        </p>
        <a
          data-testid="download-how-it-works-pdf"
          href={PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-2 px-4 h-10 bg-amber-400 text-black font-mono uppercase text-[12px] tracking-wider hover:bg-amber-300 transition-colors"
        >
          <Download size={14} /> Download PDF · How MindStein Works
        </a>
      </header>

      {/* credibility grid */}
      <section data-testid="credibility-grid" className="border border-[#27272A] bg-[#0A0A0A]">
        <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400">
            SOURCE_CREDIBILITY_LEDGER · SCORED 1-10
          </span>
          <span className="text-[10px] font-mono text-zinc-600">HIGHER = MORE TRUSTED</span>
        </div>
        <div className="divide-y divide-[#161616]">
          {CRED_ROWS.map((r) => (
            <div key={r.label} className="grid grid-cols-12 items-center gap-3 px-4 py-4">
              <div className="col-span-5 sm:col-span-4">
                <div className="text-sm text-white">{r.label}</div>
                <div className="text-[10px] font-mono text-zinc-500 uppercase">{r.example}</div>
              </div>
              <div className="col-span-5 sm:col-span-6">
                <div className="h-2 bg-[#141414] border border-[#27272A]">
                  <div className={`h-full ${r.bar} ${r.color}`} />
                </div>
              </div>
              <div className="col-span-2 text-right font-mono text-xl text-white tabnum">
                {r.score.toFixed(1)}
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-[#27272A] text-[11px] font-mono text-zinc-500">
          Composite rank = credibility × relevance × recency × repeat-value.
          Scores appear next to every cited source, e.g.{" "}
          <span className="text-emerald-300">SOURCE (9.5)</span> — always linked
          to the original. This ranking drives the five-phase pipeline below:
          primary plaque → grounded facts + keywords → cross-domain plaque →
          multi-tier synthesis → grounded conclusion.
        </div>
      </section>

      {/* flow diagram */}
      <section data-testid="flow-diagram" className="border border-[#27272A] bg-[#0A0A0A] p-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400 mb-4">
          PIPELINE
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {FLOW.map((step, i) => (
            <div key={step} className="flex items-center gap-2">
              <div className="border border-emerald-400/40 bg-emerald-500/[0.06] px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-emerald-200">
                {step}
              </div>
              {i < FLOW.length - 1 && <ArrowRight size={14} className="text-zinc-600" />}
            </div>
          ))}
        </div>
      </section>

      {/* five-phase pipeline — after ranking pulls in facts */}
      <section data-testid="synthesis-pipeline" className="border border-[#27272A] bg-[#0A0A0A]">
        <div className="px-4 py-3 border-b border-[#27272A]">
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400">
            FIVE-PHASE_GROUNDED_SYNTHESIS_PIPELINE · EVERY QUERY · EVERY MODE
          </span>
        </div>
        <div className="divide-y divide-[#161616]">
          {PIPELINE_STEPS.map((s) => (
            <div key={s.n} className="flex gap-4 px-4 py-4">
              <div className="font-mono text-emerald-400 text-lg shrink-0">{s.n}</div>
              <div>
                <div className="text-sm text-white font-mono uppercase tracking-wider">{s.title}</div>
                <p className="mt-1 text-[12px] text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-[#27272A] text-[11px] font-mono text-zinc-500">
          Output order is fixed: PRIMARY RANKING PLAQUE → GROUNDED FACTS →
          SEARCH KEYWORDS → CROSS-DOMAIN RANKING PLAQUE → CROSS-DOMAIN FACTS →
          MULTI-TIER SYNTHESIS → GROUNDED CONCLUSION. Every phase cites the
          ranked sources it rests on.
        </div>
      </section>

      {/* worked examples library */}
      <section data-testid="pipeline-examples" className="space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400">
          WORKED_EXAMPLES · FROM_THE_LIBRARY
        </div>
        {EXAMPLES.map((ex, i) => (
          <div key={ex.topic} data-testid={`pipeline-example-${i + 1}`} className="border border-[#27272A] bg-[#0A0A0A]">
            <div className="px-4 py-2.5 border-b border-[#27272A] text-[11px] font-mono uppercase tracking-wider text-emerald-300">
              EXAMPLE_{i + 1} · TOPIC: {ex.topic}
            </div>
            <div className="px-4 py-3 space-y-3 text-[12px] leading-relaxed">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Phase 1 · Primary Ranking Plaque</span>
                <pre className="mt-1 text-[10.5px] font-mono text-emerald-300 bg-[#050505] border border-emerald-400/20 p-2 overflow-x-auto whitespace-pre">{ex.plaque.map((r, k) => `[0${k + 1}] ${r}`).join("\n")}</pre>
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Phase 2 · Grounded Facts</span>
                <ul className="mt-0.5 space-y-1 text-zinc-300">
                  {ex.facts.map((f) => (<li key={f}>· {f}</li>))}
                </ul>
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Search Keywords</span>
                <p className="text-amber-200/90 font-mono mt-0.5 text-[11px]">{ex.keywords}</p>
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Phase 3 · Cross-Domain Ranking Plaque</span>
                <pre className="mt-1 text-[10.5px] font-mono text-sky-300 bg-[#050505] border border-sky-400/20 p-2 overflow-x-auto whitespace-pre">{ex.crossPlaque.map((r, k) => `[0${k + 1}] ${r}`).join("\n")}</pre>
                <ul className="mt-1 space-y-1 text-zinc-300">
                  {ex.crossFacts.map((f) => (<li key={f}>· {f}</li>))}
                </ul>
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Phase 4 · Multi-Tier Synthesis</span>
                <ul className="mt-0.5 space-y-1 text-zinc-300">
                  {ex.tiers.map((t) => (<li key={t}>· {t}</li>))}
                </ul>
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-400">Phase 5 · Grounded Conclusion</span>
                <p className="text-zinc-200 mt-0.5">{ex.conclusion}</p>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* synthesis explainer */}
      <section data-testid="synthesis-explainer" className="border border-amber-400/30 bg-amber-500/[0.04] p-5">
        <p className="text-sm text-zinc-300 leading-relaxed">
          <span className="font-mono text-amber-300">[SYNTHESIS]</span> tags mark
          where MindStein&apos;s reasoning goes beyond the sources — original
          hypotheses and cross-domain connections built on top of the verified
          facts, never presented as fact themselves.
        </p>
      </section>
    </div>
  );
}
